/* eslint-disable react/no-danger */
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconEditSmall } from '@sololearnorg/sol';
import { Container, AuthService } from '../../../../symphony';

import { Meta } from '../Meta/Meta';
import { Voting } from '../Voting/Voting';
import { SlDiscussContext } from '../../global-constants';
import {
	LinkClickSource,
	ActionType,
	RequestType,
} from '../../global-interface';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { SlDataService } from '../../services/sl-data.service';
import { QuestionMeta } from './QuestionMeta';
import { useComponentWillMount } from '../../../../shared/public/SlUtils/useComponentWillMount';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { SlMessageDrawer } from '../../../../shared/public/SlMessageDrawer/SlMessageDrawer';

import s from './Question.scss';
import { LocalizedLink } from '../../../../shared/public/LocalizedLink/LocalizedLink';

export function Question() {
	useStyles(s);
	const { id: questionId, alias: questionAlias } = useParams();
	const { actions$ } = Container.take(SlDiscussContext, SlActions);
	const isLoggedIn = Container.take('global', AuthService).isLoggedIn();

	useComponentWillMount(() => {
		actions$.next({
			type: ActionType.OnDiscussQuestionMount,
			requestType: RequestType.Parallel,
			data: {
				questionId,
				questionAlias,
			},
		});
	});

	useEffect(() => {
		window.scrollTo(0, 0);

		return () => {
			actions$.next({
				type: ActionType.OnDiscussQuestionUnMount,
				requestType: RequestType.Parallel,
			});
		};
	}, []);

	const { question$: question, isMyQuestion$: isMyQuestion } = useContainerData(SlDiscussContext, SlDataService, [
		'question$',
		'replies$',
		'isMyQuestion$',
	]);

	const handleEditClick = () => {
		actions$.next({
			type: ActionType.OnQuestionEditClick,
			requestType: RequestType.Parallel,
		});
	};

	const handleJumpLinkClick = (
		linkClickSource: LinkClickSource,
		text: string,
		ctaURL: string,
	): void => {
		actions$.next({
			type: ActionType.OnJumpLinkClick,
			requestType: RequestType.Parallel,
			data: {
				linkClickSource,
				text,
				ctaURL,
			},
		});
	};

	return question ? (
		<>
			<QuestionMeta />
			<div className="discuss-question">
				{isMyQuestion && (
					<IconEditSmall
						className="discuss-question__edit-button"
						onClick={handleEditClick}
					/>
				)}
				<div className="discuss-question__container">
					<Voting
						voteCount={question.votes}
						userVoteCount={question.vote}
						postId={question.id}
					/>
					<div className="discuss-question__main">
						<h1>
							{isLoggedIn ? (
								question.title
							) : (
								<SlMessageDrawer
									message={question.title}
									options={{
										enableJumpLink: true,
										enableLinkMessage: false,
										enableUserMention: false,
									}}
									callbacks={{
										handleJumpLinkClick: (text, link) => handleJumpLinkClick(
											LinkClickSource.TITLE,
											text,
											link,
										),
									}}
								/>
							)}
						</h1>

						{!!question.message && (
							<p className="discuss-question__main__description">
								<SlMessageDrawer
									message={question.message}
									options={{
										enableJumpLink: !isLoggedIn,
										enableLinkMessage: true,
										enableUserMention: true,
									}}
									callbacks={{
										handleJumpLinkClick: (text, link) => handleJumpLinkClick(
											LinkClickSource.DESCRIPTION,
											text,
											link,
										),
									}}
								/>
							</p>
						)}
						<div className="discuss-question__main__tags">
							{question.tags.map((tag) => (
								<LocalizedLink
									to={`/Discuss/?query=${tag}`}
									className="discuss-question__main__tags__tag"
									key={tag}
								>
									{tag}
								</LocalizedLink>
							))}
						</div>
					</div>
				</div>
				<Meta
					date={question.date}
					avatarUrl={question.avatarUrl}
					userName={question.userName}
					userID={question.userID}
					key={question.id}
					badge={question.badge}
				/>
			</div>
		</>
	) : null;
}
